'use client';

import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import Head from 'next/head';
import CarouselSection from '../../components/CarouselSection';
import Footer from '../../components/Footer';
import GenreItem from '../../components/GenreItem';
import Loading from '../../components/Loading';
import LoadingContainer from '../../components/LoadingContainer';
import ResourceItem from '../../components/ResourceItem';
import WatchProviderComponent from '../../components/WatchProvider';
import Wrapper from '../../components/Wrapper';
import { amplitudeInstance } from '../../config/amplitude';
import env from '../../config/env';
import AuthContext from '../../context/AuthContext';
import buildRouteUrl from '../../helpers/buildRouteUrl';
import useIntl from '../../hooks/useIntl';
import { Link } from '../../i18n/navigation';
import api from '../../services/api';
import { CatalogSection, Genre, WatchProvider } from '../../types/types';
import buildTitleUrl from '../../helpers/buildTitleUrl';
interface PropTypes {
  browse: any;
  genres: any;
}
function ClientBrowse(t0) {
  const $ = _c(30);
  const {
    browse,
    genres
  } = t0;
  const queryClient = useQueryClient();
  const auth = useContext(AuthContext);
  const intl = useIntl();
  const t = useTranslations();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["genres"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== genres) {
    t2 = {
      queryKey: t1,
      queryFn: _temp,
      gcTime: 100000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: 100000,
      initialData: genres
    };
    $[1] = genres;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const genresQuery = useQuery(t2);
  let t3;
  if ($[3] !== auth.isLoggedIn || $[4] !== queryClient) {
    t3 = () => {
      if (auth.isLoggedIn) {
        queryClient.prefetchQuery({
          queryKey: ["user-tv-to-watch-next-all"],
          queryFn: _temp2
        });
        queryClient.prefetchQuery({
          queryKey: ["user-tv-to-watch-next-watching"],
          queryFn: _temp3
        });
        queryClient.prefetchQuery({
          queryKey: ["user-tv-to-watch-next-not-started"],
          queryFn: _temp4
        });
        queryClient.prefetchQuery({
          queryKey: ["user-tv-to-watch-next-stopped-watching"],
          queryFn: _temp5
        });
      }
    };
    $[3] = auth.isLoggedIn;
    $[4] = queryClient;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== auth.isLoggedIn) {
    t4 = [auth.isLoggedIn];
    $[6] = auth.isLoggedIn;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== intl.locale) {
    t5 = ["catalog", intl.locale];
    $[8] = intl.locale;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== intl.full_language || $[11] !== intl.locale) {
    t6 = async () => {
      const response_4 = await api.get("/v1/browse", {
        params: {
          region: intl.locale
        },
        headers: {
          "accept-language": intl.full_language
        }
      });
      return response_4.data;
    };
    $[10] = intl.full_language;
    $[11] = intl.locale;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== browse || $[14] !== t5 || $[15] !== t6) {
    t7 = {
      queryKey: t5,
      queryFn: t6,
      gcTime: 100000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchInterval: 100000,
      initialData: browse
    };
    $[13] = browse;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const catalogQuery = useQuery(t7);
  const isLoading = genresQuery.isLoading || catalogQuery.isLoading;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Head><meta key="og:image" name="og:image" content="https://trakio.app/assets/og-preview.jpg" /><link rel="canonical" href={`${env.WEBAPP_URL}/`} /></Head>;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== isLoading) {
    t9 = isLoading && <LoadingContainer><Loading /></LoadingContainer>;
    $[18] = isLoading;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== catalogQuery || $[21] !== genresQuery.data?.data || $[22] !== intl.locale || $[23] !== isLoading || $[24] !== t) {
    t10 = !isLoading && <div className="flex flex-col pl-4 md:pl-10 w-full bg-gray-600 min-h-[calc(100vh-64px-60px)]">{(genresQuery.data?.data.length || 0) > 0 && <CarouselSection id="genres" title={t("home.genres")} type="genre" totalItems={genresQuery.data?.data.length || 0} className="mt-3">{genresQuery.data?.data.map(_temp6)}</CarouselSection>}<div className="md:hidden flex flex-row gap-x-2 mt-6"><a href="https://play.google.com/store/apps/details?id=com.cinehubapp" target="_blank" rel="noopener noreferrer" className="inline-block"><img src="/assets/app/googleplay-badge.png" alt="Get it on Google Play" className="h-10" loading="lazy" /></a><a href="https://apps.apple.com/us/app/trakio-track-shows-movies/id6749806745" target="_blank" rel="noopener noreferrer" className="inline-block h-12"><img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" className="h-10" loading="lazy" /></a></div>{(catalogQuery.data?.data.length || 0) > 0 && catalogQuery.data?.data.map(section => {
        if (section.type === "content") {
          return <CarouselSection id={section.slug} key={section.title} title={section.title} type="resource" watchProvider={section.watch_provider} totalItems={section.data.length} className="mt-6" slug={section.slug}>{section.data.map(item => {
              const url = buildTitleUrl({
                type: item.type,
                locale: intl.locale.toLowerCase(),
                shortId: item.short_id,
                slugs: item.slugs
              });
              return <div key={item._id} className="embla__slide"><Link scroll={true} href={url} prefetch={false}><ResourceItem className="mr-2" data={item} onClick={() => {
                    amplitudeInstance.logEvent("Go To Content Page From Catalog List", {
                      content_id: item._id,
                      content_type: item.type,
                      catalog_list_id: section._id,
                      catalog_list_slug: section.slug,
                      auto_generated: section.auto_generated
                    });
                  }} /></Link></div>;
            })}</CarouselSection>;
        }
        if (section.type === "watch_providers") {
          return <CarouselSection id="watch_providers" key={section.title} title={section.title} type="watch_provider" totalItems={section.data.length} className="mt-6">{(section.data as unknown as WatchProvider[]).map(_temp7)}</CarouselSection>;
        }
      })}</div>;
    $[20] = catalogQuery;
    $[21] = genresQuery.data?.data;
    $[22] = intl.locale;
    $[23] = isLoading;
    $[24] = t;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Footer />;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t9) {
    t12 = <Wrapper>{t8}{t9}{t10}{t11}</Wrapper>;
    $[27] = t10;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}
function _temp7(item_0) {
  return <div key={item_0._id} className="embla__slide"><Link scroll={true} href={buildRouteUrl(`/watch-providers/${item_0.slug}/all`)} onClick={() => {
      amplitudeInstance.logEvent("Go To Watch Provider Contents Page From Catalog List", {
        watch_provider_name: item_0.name,
        watch_provider_slug: item_0.slug,
        watch_provider_id: item_0._id
      });
    }}><WatchProviderComponent className="mr-2" data={item_0} alt={item_0.name} /></Link></div>;
}
function _temp6(genre) {
  return <div key={genre.id} className="embla__slide"><Link scroll={true} href={buildRouteUrl(`/genres/${genre.id}/movies`)} onClick={() => {
      amplitudeInstance.logEvent("Go To Genre Contents Page From Catalog List", {
        genre_id: genre.id,
        auto_generated: true
      });
    }}><GenreItem className="mr-2" data={genre} /></Link></div>;
}
async function _temp5() {
  const response_3 = await api.get("/v1/me/tv-to-watch-next", {
    params: {
      filter: "stopped_watching"
    }
  });
  return response_3.data;
}
async function _temp4() {
  const response_2 = await api.get("/v1/me/tv-to-watch-next", {
    params: {
      filter: "not_started"
    }
  });
  return response_2.data;
}
async function _temp3() {
  const response_1 = await api.get("/v1/me/tv-to-watch-next", {
    params: {
      filter: "watching"
    }
  });
  return response_1.data;
}
async function _temp2() {
  const response_0 = await api.get("/v1/me/tv-to-watch-next", {
    params: {
      filter: "all"
    }
  });
  return response_0.data;
}
async function _temp() {
  const response = await api.get("/v1/genres");
  return response.data;
}
export default ClientBrowse;